import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Textarea } from 'rizzui';
import useFetch from '../../../../hooks/useFetch';
import FormGroup from "../../../../layout/shared/form-group";
import moment from 'moment';
import {InvoiceService} from "../../../../services/invoices/invoiceService";
import {InvoiceApiResponse} from "../../../../type/entities/invoice-type";

interface InvoiceInfoProps { }

const InvoiceInfo: React.FC<InvoiceInfoProps> = () => {

    // HOOKS
    
    const { id = '' } = useParams<{ id: string }>();
    const service = new InvoiceService();

    // DATA FETCHING

    // -----------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene los datos de la factura por ID
     *
     * @EN Gets the invoice data by ID
     */
    // -----------------------------------------------------------------------------------------------------
    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getInvoiceById(id);
        return response.getResponseData() as InvoiceApiResponse;
    }, [id]));
    // -----------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <>
            <div className="@container">
                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                    <FormGroup
                        title="Cliente"
                        description="Datos del cliente"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            disabled
                            label="Razón Social"
                            className="md:col-span-2"
                            value={data?.client?.companyName || ''}
                        />

                        <Input
                            disabled
                            label="CIF/NIF"
                            className="md:col-span-1"
                            value={data?.client?.cifNif || ''}
                        />

                        <Input
                            disabled
                            label="Dirección"
                            className="md:col-span-1"
                            value={data?.address || ''}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Información de la Proforma"
                        description="Datos principales de la proforma"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            disabled
                            label="Código de factura"
                            className="md:col-span-1"
                            value={data?.serialCode || ''}
                        />

                        <Input
                            disabled
                            label="Número de control"
                            className="md:col-span-1"
                            value={data?.serialCodeNumberControl?.toString() || ''}
                        />

                        <Input
                            disabled
                            label="Fecha de facturación"
                            className="md:col-span-1"
                            value={data?.invoiceDate ? moment(data.invoiceDate.date).format('DD/MM/YYYY') : ''}
                        />

                        <Input
                            disabled
                            label="Fecha de pago"
                            className="md:col-span-1"
                            value={data?.paymentDate ? moment(data.paymentDate.date).format('DD/MM/YYYY') : ''}
                        />

                        <Input
                            disabled
                            label="Cantidad (Kg)"
                            className="md:col-span-1"
                            value={data?.kgQuantity ? `${data.kgQuantity} Kg` : ''}
                        />

                        <Input
                            disabled
                            label="Precio Unitario (€)"
                            className="md:col-span-1"
                            value={data?.unitPrice ? `€ ${data.unitPrice.toFixed(2)}` : ''}
                        />

                        <Input
                            disabled
                            label="IVA (%)"
                            className="md:col-span-1"
                            value={data?.ivaTax ? `${data.ivaTax} %` : ''}
                        />

                        <Input
                            disabled
                            label="Importe Total (€)"
                            className="md:col-span-1"
                            value={data?.totalAmount ? `€ ${data.totalAmount.toFixed(2)}` : ''}
                        />

                        <Textarea
                            disabled
                            label="Comentarios"
                            className="md:col-span-4"
                            value={data?.comments || ''}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Estado de la Proforma"
                        description="Estado y fechas de cancelación"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            disabled
                            label="Estado de Pago"
                            className="md:col-span-1"
                            value={data?.isPaid ? 'Pagada' : 'Pendiente'}
                        />

                        <Input
                            disabled
                            label="Estado de Proforma"
                            className="md:col-span-1"
                            value={data?.isCancel ? 'Cancelada' : 'Activa'}
                        />

                        <Input
                            disabled
                            label="Fecha de Cancelación"
                            className="md:col-span-1"
                            value={data?.cancelDate ? moment(data.cancelDate.date).format('DD/MM/YYYY') : 'No cancelada'}
                        />

                        <Input
                            disabled
                            label="Motivo de Cancelación"
                            className="md:col-span-1"
                            value={data?.cancelReason || 'N/A'}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Fechas del Sistema"
                        description="Fechas de creación y actualización"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-2"
                    >
                        <Input
                            disabled
                            label="Fecha de Creación"
                            className="md:col-span-1"
                            value={data?.createdAt ? moment(data.createdAt.date).format('DD/MM/YYYY HH:mm') : ''}
                        />

                        <Input
                            disabled
                            label="Fecha de Actualización"
                            className="md:col-span-1"
                            value={data?.updatedAt ? moment(data.updatedAt.date).format('DD/MM/YYYY HH:mm') : ''}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Organización"
                        description="Datos de la organización"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        <Input
                            disabled
                            label="Nombre"
                            value={data?.company?.name ?? ""}
                        />
                        <Input
                            disabled
                            label="CIF"
                            value={data?.company?.cif ?? ""}
                        />
                    </FormGroup>
                </div>
            </div>
        </>
    );
};

export default InvoiceInfo;